import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, styled, Table, TableBody, TableCell, TableRow as MuiTableRow, Typography, Divider } from '@mui/material';
import { Activity, CustomField, CustomFieldOption, CustomFieldValue, SystemFieldId, useFindCustomFieldsQuery, useFindThemesWithSpecificationsQuery, useRealitiesDataQuery } from 'gql';
import React from 'react';
import { useIntl } from 'react-intl';


type FieldValue = Pick<CustomFieldValue, 'displayString'> & {
  field: Pick<CustomField, 'id' | 'systemFieldId'>;
  selectionValues: Pick<CustomFieldOption, 'id'>[];
};

type Reality = Pick<Activity['realities'][0], 'id' | 'themeId' | 'specificationId'> & {
  values: FieldValue[];
};

interface Props {
  realities: Reality[];
}

const TableRow = styled(MuiTableRow)(({ theme }) => ({
  '& td, & th': { borderColor: theme.palette.divider },
  '&:last-child td, &:last-child th': { border: 0 }
}));

export const ActivityRealities: React.FC<Props> = ({ realities }) => {
  const [expanded, setExpanded] = React.useState<number | false>();

  const { formatMessage } = useIntl();

  const themeIds = realities.map(r => r.themeId);
  const { data: themeData } = useFindThemesWithSpecificationsQuery({
    filter: { id: { in: themeIds } }
  });

  const fieldIds = realities.flatMap(r => r.values.map(v => v.field.id));
  const { data: fieldsData } = useFindCustomFieldsQuery({
    filter: { id: { in: fieldIds } }
  });
  const fields = fieldsData?.customFields ?? [];

  const { data: realitiesData } = useRealitiesDataQuery();
  const referralOptions = realitiesData?.referrals ?? [];

  const handleChange = (panel: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpanded(panel);
    } else {
      setExpanded(false);
    }
  };

  return <>
    {realities.map((reality, index) => {
      const theme = themeData?.themes.find(t => t.id === reality.themeId);
      const specification = theme?.specifications.find(s => s.id === reality.specificationId);



      const accompaniment = reality.values.filter(v => v.field.systemFieldId === SystemFieldId.RealityAccompaniments).at(0);
      const accompanimentOtherValue = reality.values.filter(v => v.field.systemFieldId === SystemFieldId.AccompanimentOtherValue).at(0)?.displayString;
      const approach = reality.values.filter(v => v.field.systemFieldId === SystemFieldId.RealityApproaches).at(0);
      const approachOtherValue = reality.values.filter(v => v.field.systemFieldId === SystemFieldId.ApproachOtherValue).at(0)?.displayString;
      const referral = reality.values.filter(v => v.field.systemFieldId === SystemFieldId.RealityReferrals).at(0);
      const referralOtherValue = reality.values.filter(v => v.field.systemFieldId === SystemFieldId.ReferralOtherValue).at(0)?.displayString;

      const otherFields = reality.values.filter(v =>
        v.field.systemFieldId !== SystemFieldId.RealityAccompaniments &&
        v.field.systemFieldId !== SystemFieldId.RealityApproaches &&
        v.field.systemFieldId !== SystemFieldId.RealityReferrals &&
        v.field.systemFieldId !== SystemFieldId.ApproachOtherValue &&
        v.field.systemFieldId !== SystemFieldId.AccompanimentOtherValue &&
        v.field.systemFieldId !== SystemFieldId.ReferralOtherValue);

      return (
        <Accordion
          key={reality.id}
          expanded={expanded === reality.id}
          onChange={handleChange(reality.id)}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant='h6'>{formatMessage({ id: 'Reality {index}' }, { index: index + 1 })}</Typography>
          </AccordionSummary>
          <Divider />
          <AccordionDetails sx={{ p: 0 }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell variant='head'>{formatMessage({ id: 'Theme' })}</TableCell>
                  <TableCell>{theme?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant='head'>{formatMessage({ id: 'Specification' })}</TableCell>
                  <TableCell>{specification?.name}</TableCell>
                </TableRow>
                {(approach && approach.selectionValues.length > 0) && (
                  <TableRow>
                    <TableCell variant='head'>{formatMessage({ id: 'Approaches' })}</TableCell>
                    <TableCell>{approach.displayString}</TableCell>
                  </TableRow>
                )}
                {(approachOtherValue && approachOtherValue.length > 0) && (
                  <TableRow>
                    <TableCell variant='head'>{formatMessage({ id: 'Other approach' })}</TableCell>
                    <TableCell>{approachOtherValue}</TableCell>
                  </TableRow>
                )}
                {(referral && referral.selectionValues.length > 0) && (
                  <TableRow>
                    <TableCell variant='head'>{formatMessage({ id: 'Referrals' })}</TableCell>
                    <TableCell>{referral
                      .selectionValues
                      .map(a => referralOptions.find(o => o.id == a.id)?.name)
                      .join(', ')
                    }</TableCell>
                  </TableRow>
                )}
                {(referralOtherValue && referralOtherValue.length > 0) && (
                  <TableRow>
                    <TableCell variant='head'>{formatMessage({ id: 'Other referral' })}</TableCell>
                    <TableCell>{referralOtherValue}</TableCell>
                  </TableRow>
                )}
                {(accompaniment && accompaniment.selectionValues.length > 0) && (
                  <TableRow>
                    <TableCell variant='head'>{formatMessage({ id: 'Accompaniments' })}</TableCell>
                    <TableCell>{accompaniment
                      .selectionValues
                      .map(a => referralOptions.find(o => o.id == a.id)?.name)
                      .join(', ')
                    }</TableCell>
                  </TableRow>
                )}
                {(accompanimentOtherValue && accompanimentOtherValue.length > 0) && (
                  <TableRow>
                    <TableCell variant='head'>{formatMessage({ id: 'Other accompaniment' })}</TableCell>
                    <TableCell>{accompanimentOtherValue}</TableCell>
                  </TableRow>
                )}
                {otherFields.map(value => {
                  return (
                    <TableRow key={value.field.id}>
                      <TableCell variant='head'>{fields.find(f => f.id == value.field.id)?.name}</TableCell>
                      <TableCell>{value.displayString}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      );
    })}
  </>;
};